import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Share2, MessageCircle } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';

function parseInline(text) {
  const parts = text.split(/(\*\*.*?\*\*|__.*?__)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('__') && part.endsWith('__'))
      return <u key={i}>{part.slice(2, -2)}</u>;
    return part;
  });
}

function renderFooterInfoLine(line, idx) {
  if (line.startsWith('- ')) {
    return (
      <p key={idx} className="flex gap-1.5">
        <span className="shrink-0">•</span>
        <span>{parseInline(line.slice(2))}</span>
      </p>
    );
  }
  return <p key={idx}>{parseInline(line)}</p>;
}

const Footer = () => {
  const { settings } = useSettings();
  const currentYear = new Date().getFullYear();

  const getSocialIcon = (platform) => {
    const size = 16;
    switch (platform) {
      case 'instagram':
      case 'facebook':
      case 'tiktok':
      case 'youtube':
        return <Share2 size={size} />;
      case 'whatsapp':
      case 'telegram':
        return <MessageCircle size={size} />;
      default:
        return <Mail size={size} />;
    }
  };

  const renderSocialLink = (platform, url) => {
    if (!url) return null;

    const isWhatsApp = platform === 'whatsapp' && url.match(/^\+?[0-9]/);
    const href = isWhatsApp ? `https://wa.me/${url.replace(/\D/g, '')}` : url;
    const target = isWhatsApp ? '_blank' : '_blank';

    return (
      <a
        key={platform}
        href={href}
        target={target}
        rel="noopener noreferrer"
        className="flex items-center gap-2 hover:text-brand-main transition-colors capitalize"
      >
        {getSocialIcon(platform)}
        {platform}
      </a>
    );
  };

  return (
    <footer className="bg-brand-dark text-brand-light mt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <h3 className="font-display text-2xl italic font-bold mb-2">{settings.site_name}</h3>
            <p className="font-body text-sm text-brand-light/70">
              {settings.artist_bio}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4">Navigazione</h4>
            <nav className="space-y-2 font-body text-sm">
              <Link to="/" className="block hover:text-brand-main transition-colors">
                Home
              </Link>
              <Link to="/galleria" className="block hover:text-brand-main transition-colors">
                Galleria
              </Link>
              <Link to="/about" className="block hover:text-brand-main transition-colors">
                About
              </Link>
              <Link to="/contatti" className="block hover:text-brand-main transition-colors">
                Contatti
              </Link>
            </nav>
          </div>

          {/* Info & Orari */}
          {settings.footer_info && (
            <div>
              <h4 className="font-display text-lg font-semibold mb-4">
                {settings.footer_info_title || 'Info & Orari'}
              </h4>
              <div className="space-y-1 font-body text-sm text-brand-light/70">
                {settings.footer_info.split('\n').map((line, idx) => renderFooterInfoLine(line, idx))}
              </div>
            </div>
          )}

          {/* Social & Contact */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4">Connettiti</h4>
            <div className="space-y-3 font-body text-sm">
              {settings.contact_email && (
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="flex items-center gap-2 hover:text-brand-main transition-colors"
                >
                  <Mail size={16} />
                  {settings.contact_email}
                </a>
              )}

              {settings.contact_address && (
                <div className="flex items-center gap-2 text-brand-light/70">
                  <MapPin size={16} />
                  {settings.contact_address}
                </div>
              )}

              <div className="border-t border-brand-light/20 pt-3 mt-3">
                {renderSocialLink('instagram', settings.social_instagram)}
                {renderSocialLink('facebook', settings.social_facebook)}
                {renderSocialLink('whatsapp', settings.social_whatsapp)}
                {renderSocialLink('telegram', settings.social_telegram)}
                {renderSocialLink('tiktok', settings.social_tiktok)}
                {renderSocialLink('youtube', settings.social_youtube)}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-brand-light/20 pt-8">
          <p className="font-body text-sm text-center text-brand-light/60">
            © {currentYear} {settings.site_name}. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
